import { z } from 'zod';
import type { BenchmarkQuery, BenchmarkRecord, BenchmarkResult } from './contracts';

export const BenchmarkRecordSchema = z
  .object({
    id: z.string().min(1),
    metric: z.string().min(1),
    unit: z.string().min(1),
    value: z.number().finite().optional(),
    range: z.object({ min: z.number().finite(), max: z.number().finite() }).optional(),
    source: z.object({
      name: z.string().min(1),
      url: z.string().url().optional(),
      publishedAt: z.string().min(1).optional(),
    }),
    context: z.object({
      category: z.string().min(1),
      segment: z.string().min(1).optional(),
      geography: z.string().min(1).optional(),
      channel: z.string().min(1).optional(),
      period: z.string().min(1).optional(),
    }),
    confidence: z.number().min(0).max(1),
  })
  .refine((record) => record.value !== undefined || record.range !== undefined, {
    message: 'A benchmark requires a value or range.',
  })
  .refine((record) => !record.range || record.range.min <= record.range.max, {
    message: 'Benchmark range is invalid.',
  });

export function validateBenchmark(candidate: unknown): BenchmarkRecord {
  return BenchmarkRecordSchema.parse(candidate);
}

const OPTIONAL_CONTEXT = ['segment', 'geography', 'channel', 'period'] as const;

export function lookupBenchmarks(
  records: readonly BenchmarkRecord[],
  query: BenchmarkQuery,
): BenchmarkResult {
  const sameMetric = records.filter((record) => record.metric === query.metric);
  if (sameMetric.length === 0) {
    return {
      status: 'not_found',
      matches: [],
      missingContext: [],
      explanation: 'No sourced benchmark is available for this metric.',
    };
  }
  const compatibleUnit = sameMetric.filter((record) => record.unit === query.unit);
  if (compatibleUnit.length === 0) {
    return {
      status: 'not_comparable',
      matches: [],
      missingContext: [],
      explanation: 'Available benchmarks use an incompatible unit.',
    };
  }
  const compatibleCategory = compatibleUnit.filter(
    (record) => record.context.category === query.context.category,
  );
  if (compatibleCategory.length === 0) {
    return {
      status: 'not_comparable',
      matches: [],
      missingContext: ['category'],
      explanation: 'Available benchmarks describe a different category.',
    };
  }
  const ranked = compatibleCategory
    .map((record) => {
      const conflicting = OPTIONAL_CONTEXT.some(
        (key) =>
          query.context[key] && record.context[key] && query.context[key] !== record.context[key],
      );
      const missing = OPTIONAL_CONTEXT.filter((key) => query.context[key] && !record.context[key]);
      const exact = OPTIONAL_CONTEXT.filter(
        (key) => query.context[key] && query.context[key] === record.context[key],
      ).length;
      return { record, conflicting, missing, exact };
    })
    .filter((entry) => !entry.conflicting)
    .sort(
      (left, right) =>
        right.exact - left.exact ||
        right.record.confidence - left.record.confidence ||
        left.record.id.localeCompare(right.record.id),
    );
  if (ranked.length === 0) {
    return {
      status: 'not_comparable',
      matches: [],
      missingContext: [],
      explanation: 'Available benchmarks conflict with the requested context.',
    };
  }
  const best = ranked[0]!;
  return {
    status: best.missing.length ? 'partial_match' : 'matched',
    matches: [best.record],
    missingContext: [...best.missing],
    explanation: best.missing.length
      ? `Closest sourced benchmark is missing: ${best.missing.join(', ')}.`
      : 'A sourced benchmark matches the requested context.',
  };
}
